/**
 * FlowReport Component
 *
 * A comprehensive flow report component that supports both single-flow and multi-flow modes.
 * Displays execution summaries, action statistics charts, and detailed grid data.
 *
 * @fileoverview
 * This is the main entry point for flow reports in the application.
 *
 * Features:
 * - Single-flow mode: Reports for one flow (e.g., SafeSend)
 * - Multi-flow mode: Combined reports for multiple related flows (e.g., 8879)
 * - Date range filtering
 * - Server-side paginated grid with filtering and sorting
 * - Action statistics chart (Highcharts)
 * - Detail modal for viewing record details and updating user actions
 *
 * Configuration:
 * - Single-flow: Uses flowId parameter
 * - Multi-flow: Uses reportType parameter with config from tblFirmReferenceData
 *
 * @example
 * // Single-flow usage
 * <FlowReport
 *   reportMode="single"
 *   flowId={85}
 *   flowName="SafeSend Report"
 *   reportEndpoint="/workflow/flow/report/"
 *   gridEndpoint="/workflow/flow/grid/"
 * />
 *
 * @example
 * // Multi-flow usage
 * <FlowReport
 *   reportMode="multi"
 *   reportType="8879"
 *   multiFlowConfig={config}
 *   flowName="E-file Signature Authorization (8879) Report"
 *   reportEndpoint="/workflow/flow/multi-report/"
 *   gridEndpoint="/workflow/flow/multi-grid/"
 * />
 */

import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { ColDef } from "ag-grid-community";
import * as Icons from "react-bootstrap-icons";
import { Modal } from "react-bootstrap";
import DateTime from "../../reusable-components/InputFields/elements/Date";
import styles from "./FlowReport.module.css";
import {
  FlowReport as FlowReportType,
  ReportItem,
  ReportSection,
  formatValue,
} from "../../reusable-components/WorkFlowModals/FlowReportModal/FlowReportModal.utils";
import ActionStatsChart from "./ActionChart";
import CygnusGrid from "../../reusable-components/CygnusGrid/CygnusGrid";
import {
  CygnusGridRef,
  ExportFormat,
  GridApiProps,
  append,
} from "../../reusable-components/CygnusGrid/CygnusGrid.utils";
import {
  formatDateLocal,
  formatDateLocalFull,
  getStartOfYear,
  getTimezoneDisplay,
  getToday,
  toDateOnlyIso,
} from "./FlowReport.utils";

// Import DetailModal components
import DetailModalBody from "./DetailModalBody";
import { getStatusColor } from "./FlowReport.utils";
import ELFUnitDetailsCell from "./ELFUnitDetailsCell";
import { toast } from "react-toastify";
import { logout } from "../../utils";

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export type FlowReportGridRow = Record<string, any>;

/**
 * Grid column definition from backend
 */
export interface GridColumn {
  field: string;
  header: string;
  headerName?: string;
  type: "string" | "number" | "date" | "status";
  sortable?: boolean;
  filterable?: boolean;
  hide?: boolean;
  width?: number;
  flex?: number;
  filterValues?: string[];
  /** For action columns - the original action name */
  actionName?: string;
  /** For action columns - the beautified display name */
  displayName?: string;
  /** Column type for identifying action columns */
  columnType?: "actionStatus" | "actionDate";
  /** For multi mode - which flow this column belongs to */
  flowSource?: string;
}

/**
 * Action statistics for chart display
 */
export interface ActionStat {
  actionName: string;
  displayName?: string;
  totalAttempts: number;
  successCount: number;
  pendingCount: number;
  failCount: number;
  successRate: number;
  /** For multi mode - which flow this action belongs to */
  flowSource?: string;
}

/**
 * Grid data structure from backend
 */
export interface FlowReportGrid {
  rows: FlowReportGridRow[];
  rowCount: number;
  page: number;
  pageSize: number;
  columns?: GridColumn[];
}

/**
 * Report data structure from backend
 */
export interface FlowReportData {
  report: FlowReportType;
  aggregates: {
    total: number;
    success: number;
    failed: number;
    pending?: number;
  };
  actionStats?: ActionStat[];
  beautifiedNames?: Record<string, string>;
  allActions?: string[] | Record<string, string[]>;
}

/**
 * Flow configuration within a multi-flow config
 */
export interface FlowConfig {
  flowId: number;
  alias: string;
  displayName: string;
  flowName?: string;
  fields?: Array<{
    source: string;
    header: string;
    type: string;
    visible: boolean;
    sortable?: boolean;
    filterable?: boolean;
    filterValues?: string[];
    width?: number;
    flex?: number;
  }>;
  includeActions?: boolean;
  actionOrder?: string[];
  excludeActions?: string[];
}

/**
 * Multi-flow configuration structure from tblFirmReferenceData
 */
export interface MultiFlowConfig {
  reportType: string;
  reportName: string;
  reportSubtitle?: string;
  joinField: string;
  fallbackJoinField?: string;
  includeReportType?: boolean;
  reportTypeHeader?: string;
  flows: FlowConfig[];
  baseColumns: Array<{
    source: string;
    header: string;
    type: string;
    visible: boolean;
  }>;
}

/**
 * Column transformer function type
 * Transforms API columns into AG-Grid column definitions
 */
export type ColumnTransformer = (
  columns: GridColumn[],
  helpers: {
    formatDateLocalFull: (value: any) => string;
    formatDateLocal: (value: any) => string;
    StatusBadge: React.FC<{ status: string }>;
    StatusDateCell: React.FC<{
      data: any;
      statusField: string;
      dateField: string;
    }>;
    beautifiedNames: Record<string, string>;
    baseColumnHeaders: Record<string, string>;
    flowsConfig: FlowConfig[];
    userActionStatuses: string[];
    onRowClick?: (rowData: any) => void;
  },
) => ColDef[];

/**
 * FlowReport component props
 */
interface FlowReportProps {
  /** Report mode: 'single' or 'multi' */
  reportMode: "single" | "multi";

  /** For single mode: Flow ID */
  flowId?: number;

  /** For multi mode: Report type (e.g., '8879') */
  reportType?: string;

  /** For multi mode: Full configuration object */
  multiFlowConfig?: MultiFlowConfig;

  /** Display name for the report */
  flowName?: string;

  /** Custom endpoint for report data */
  reportEndpoint?: string;

  /** Custom endpoint for grid data */
  gridEndpoint?: string;

  /** Callback when report loads successfully */
  onReportLoaded?: (
    report: FlowReportType,
    grid: FlowReportGrid | null,
  ) => void;

  /** Callback on error */
  onError?: (error: Error) => void;

  /** Custom loading component */
  loadingComponent?: React.ReactNode;

  /** Custom error component */
  errorComponent?: (error: Error) => React.ReactNode;

  /** Initial page size for grid */
  initialPageSize?: number;

  /** Show header with title and date filters */
  showHeader?: boolean;

  /** Show action statistics chart */
  showActionStats?: boolean;

  /** X-axis title for action stats chart */
  xaxistitle?: string;

  /** Custom column transformer */
  columnTransformer?: ColumnTransformer;

  /** Custom filter key map */
  customFilterKeyMap?: Record<string, string>;

  /** Beautified action names */
  beautifiedNames?: Record<string, string>;

  /** Base column header overrides */
  baseColumnHeaders?: Record<string, string>;

  /** Disclaimer text to show in the report header */
  disclaimerText?: string;
}

// =============================================================================
// ICON MAPPING
// =============================================================================

const ICON_MAP: Record<string, React.ElementType> = {
  check: Icons.Check2,
  x: Icons.X,
  database: Icons.Database,
  "arrow-repeat": Icons.ArrowRepeat,
  person: Icons.Person,
  clock: Icons.Clock,
  "exclamation-triangle": Icons.ExclamationTriangle,
  "info-circle": Icons.InfoCircle,
};

// =============================================================================
// SHARED UI COMPONENTS
// =============================================================================

/**
 * Status Badge Component
 * Displays colored badge based on status value.
 * Can optionally behave like a clickable button.
 */

export const StatusBadge: React.FC<{
  status: string;
  label?: string;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  title?: string;
}> = ({ status, label, onClick, title }) => {
  if (!status || status === "N/A") {
    return <span className={styles.badgeNA}>N/A</span>;
  }

  const statusLower = status.toLowerCase();
  const displayText = label || status;

  const clickable = typeof onClick === "function";

  const render = (className: string) =>
    clickable ? (
      <button
        type="button"
        className={className}
        onClick={onClick}
        title={title}
      >
        {displayText}
      </button>
    ) : (
      <span className={className} title={title}>
        {displayText}
      </span>
    );

  if (statusLower === "success" || statusLower === "accepted") {
    return render(styles.badgeSuccess);
  }

  if (
    statusLower === "fail" ||
    statusLower === "failure" ||
    statusLower === "error" ||
    statusLower === "rejected"
  ) {
    return render(styles.badgeFailure);
  }

  if (statusLower === "pending") {
    return render(styles.badgePending);
  }

  return render(styles.badgeNA);
};

/**
 * User Action Status Badge Component
 * Displays colored badge for user-defined status values
 */
export const UserActionStatusBadge: React.FC<{ status: string }> = ({
  status,
}) => {
  if (!status) return <StatusBadge status={"N/A"} />;

  const color = getStatusColor(status);

  return (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: "4px",
        backgroundColor: color,
        color: "white",
        fontSize: "11px",
        fontWeight: 500,
      }}
    >
      {status}
    </span>
  );
};

/**
 * Status + Date Cell Component
 * Combines status badge with date below it
 */
/**
 * Status + Date Cell Component
 * Combines status badge with date below it
 */
export const StatusDateCell: React.FC<{
  data: any;
  statusField: string;
  dateField: string;
}> = ({ data, statusField, dateField }) => {
  const status = data?.[statusField];
  const date = data?.[dateField];

  if (!status && !date) {
    return <span className={styles.emptyCell}>—</span>;
  }

  const statusLower = (status || "").toLowerCase();

  return (
    <div className={styles.statusDateCell}>
      <StatusBadge status={status || "N/A"} />
      {date && statusLower !== "pending" && statusLower !== "n/a" && (
        <span className={styles.statusDate}>{formatDateLocal(date)}</span>
      )}
      {statusLower === "pending" && (
        <span className={styles.statusDatePending}>Awaiting</span>
      )}
    </div>
  );
};

/**
 * Metric Card Component
 */
const MetricCard: React.FC<ReportItem> = ({
  label,
  value,
  format,
  icon,
  color,
  tooltip,
}) => {
  const Icon = icon ? ICON_MAP[icon] : null;
  return (
    <div
      className={`${styles.metricCard} ${color ? styles[color] : ""}`}
      title={tooltip}
    >
      <div className={styles.metricHeader}>
        {Icon && <Icon size={18} />}
        <span>{label}</span>
      </div>
      <div className={styles.metricValue}>{formatValue(value, format)}</div>
    </div>
  );
};

/**
 * Chip Component
 */
const Chip: React.FC<ReportItem> = ({ label, value, icon, color, tooltip }) => {
  const Icon = icon ? ICON_MAP[icon] : null;
  return (
    <div
      className={`${styles.chip} ${color ? styles[color] : ""}`}
      title={tooltip}
    >
      {Icon && <Icon size={14} />}
      <span>{label}</span>
      <strong>{formatValue(value, "text")}</strong>
    </div>
  );
};

// =============================================================================
// DEFAULT COLUMN TRANSFORMER
// =============================================================================

/**
 * Default column transformer
 * Handles action columns by combining status + date into single cells
 * Supports both single-flow and multi-flow column patterns
 * Dynamically adds UserActionStatus columns based on flowsConfig
 */
const defaultColumnTransformer: ColumnTransformer = (
  columns,
  {
    formatDateLocal,
    StatusBadge,
    StatusDateCell,
    beautifiedNames,
    baseColumnHeaders,
    flowsConfig,
    userActionStatuses,
    onRowClick,
  },
) => {
  // Categorize columns dynamically
  const actionColumns: GridColumn[] = [];
  const userActionCols: GridColumn[] = [];
  const baseColumns: GridColumn[] = [];

  columns.forEach((col) => {
    const field = col.field;

    // Action STATUS columns (we render these with StatusDateCell)
    if (
      (field.includes("_action_") || field.startsWith("action_")) &&
      field.endsWith("_status")
    ) {
      actionColumns.push(col);
    }
    // Action DATE columns - SKIP (used internally by StatusDateCell)
    else if (
      (field.includes("_action_") || field.startsWith("action_")) &&
      field.endsWith("_date")
    ) {
      // Intentionally empty - don't add to any column group
    }
    // User Action columns
    else if (field === "userActionStatus" || field === "userComment") {
      userActionCols.push(col);
    }
    // Everything else is a base column
    else {
      baseColumns.push(col);
    }
  });

  // Helper to transform a column based on its field/type
  const transformColumn = (col: GridColumn): ColDef | null => {
    const colDef: ColDef = {
      headerName:
        baseColumnHeaders?.[col.field] || col.headerName || col.header,
      field: col.field,
      sortable: col.sortable !== false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      enableRowGroup: true,
      enablePivot: true,
      enableValue: true,
    };

    if (col.width) colDef.width = col.width;
    if (col.flex) colDef.flex = col.flex;

    const fieldLower = col.field.toLowerCase();

    // Client ID
    if (fieldLower === "clientid") {
      colDef.minWidth = 100;
      colDef.maxWidth = 120;
      colDef.filter = "agTextColumnFilter";
      colDef.filterParams = {
        filterOptions: ["contains"],
        defaultOption: "contains",
        suppressAndOrCondition: true,
      };
    }
    // Client Name - Clickable button to open detail modal
    else if (fieldLower === "clientname") {
      colDef.minWidth = 180;
      colDef.flex = 2;
      colDef.filter = "agTextColumnFilter";
      colDef.filterParams = {
        filterOptions: ["contains"],
        defaultOption: "contains",
        suppressAndOrCondition: true,
      };
      colDef.cellRenderer = (params: any) => {
        if (!params.value) return <span className={styles.emptyCell}>—</span>;

        return (
          <button
            type="button"
            className={styles.clientNameLink}
            onClick={(e) => {
              e.stopPropagation();
              if (onRowClick) {
                onRowClick(params.data);
              }
            }}
          >
            {params.value}
          </button>
        );
      };
    }
    // Overall Status
    else if (fieldLower === "overallstatus") {
      colDef.filter = "agSetColumnFilter";
      colDef.filterParams = {
        values: col.filterValues || ["SUCCESS", "FAIL", "PENDING"],
      };
      colDef.cellRenderer = (params: any) => (
        <StatusBadge status={params.value || "N/A"} />
      );
      colDef.minWidth = 110;
      colDef.maxWidth = 130;
    }
    // Status-type columns (ELFStatus, reportType, etc.)
    else if (
      fieldLower.includes("elfstatus") ||
      fieldLower.includes("reporttype") ||
      col.type === "status"
    ) {
      colDef.filter = "agSetColumnFilter";
      colDef.filterParams = {
        values: col.filterValues || [
          "SUCCESS",
          "FAIL",
          "ERROR",
          "Accepted",
          "Rejected",
          "Pending",
        ],
      };
      colDef.cellRenderer = (params: any) => (
        <StatusBadge status={params.value || "N/A"} />
      );
      colDef.minWidth = 110;
      colDef.maxWidth = 130;
    }
    // Rejection / FailedUnits / ELFUnitDetails columns
    // Rejection / FailedUnits / ELFUnitDetails columns
    else if (
      fieldLower.includes("failedunits") ||
      fieldLower.includes("rejection") ||
      fieldLower.includes("elfunitdetails")
    ) {
      // Skip ELFUnitDetails as separate column - combined with FailedUnits
      if (fieldLower.includes("elfunitdetails")) {
        return null;
      }

      colDef.headerName = "eFile Rejection Details";
      colDef.filter = "agTextColumnFilter";
      colDef.filterParams = {
        filterOptions: ["contains"],
        defaultOption: "contains",
        suppressAndOrCondition: true,
      };
      colDef.minWidth = 200;
      colDef.flex = 1;
      colDef.autoHeight = true;
      colDef.cellRenderer = (params: any) => (
        <ELFUnitDetailsCell data={params.data} onRowClick={onRowClick} />
      );
    }
    // Date columns
    else if (
      col.type === "date" ||
      fieldLower.includes("date") ||
      fieldLower.includes("updatedon") ||
      fieldLower.includes("createdat")
    ) {
      colDef.filter = "agDateColumnFilter";
      colDef.filterParams = {
        filterOptions: ["equals"],
        defaultOption: "equals",
        suppressAndOrCondition: true,
        browserDatePicker: true,
      };
      colDef.valueFormatter = (p) => formatDateLocal(p.value);
      colDef.minWidth = 140;
      colDef.maxWidth = 160;
    }
    // Default text columns
    else {
      colDef.filter = col.filterable !== false ? "agTextColumnFilter" : false;
      if (col.filterable !== false) {
        colDef.filterParams = {
          filterOptions: ["contains"],
          defaultOption: "contains",
          suppressAndOrCondition: true,
        };
      }
      colDef.minWidth = 100;
      colDef.flex = 1;
      colDef.valueFormatter = (p) =>
        p.value !== null && p.value !== undefined ? String(p.value) : "";
    }

    return colDef;
  };

  // Transform base columns (maintains API order)
  const transformedBase: ColDef[] = baseColumns
    .filter((col) => !col.hide && col.field !== "_joinKey")
    .map(transformColumn)
    .filter((col): col is ColDef => col !== null);

  // Transform action columns
  const transformedActions: ColDef[] = actionColumns.map((statusCol) => {
    const dateField = statusCol.field.replace("_status", "_date");
    const headerName = statusCol.headerName || statusCol.header || "Action";

    return {
      headerName: headerName,
      field: statusCol.field,
      sortable: false,
      filter: false,
      floatingFilter: false,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      minWidth: 160,
      width: 175,
      autoHeight: true,
      cellRenderer: (params: any) => (
        <StatusDateCell
          data={params.data}
          statusField={statusCol.field}
          dateField={dateField}
        />
      ),
    } as ColDef;
  });

  // Transform user action columns
  const transformedUserAction: ColDef[] = userActionCols
    .map((col) => {
      if (col.field === "userActionStatus") {
        return {
          headerName: "User Status",
          field: col.field,
          sortable: true,
          filter: "agSetColumnFilter",
          filterParams: {
            values:
              userActionStatuses.length > 0
                ? userActionStatuses
                : [
                    "Pending Review",
                    "Skipped",
                    "Processed",
                    "Resolved",
                    "Escalated",
                    "On Hold",
                  ],
          },
          cellRenderer: (params: any) => (
            <UserActionStatusBadge status={params.value} />
          ),
          minWidth: 130,
          maxWidth: 150,
          wrapHeaderText: true,
          autoHeaderHeight: true,
        } as ColDef;
      }

      if (col.field === "userComment") {
        return {
          headerName: "User Comment",
          field: col.field,
          sortable: false,
          filter: "agTextColumnFilter",
          filterParams: {
            filterOptions: ["contains"],
            defaultOption: "contains",
            suppressAndOrCondition: true,
          },
          minWidth: 150,
          flex: 1,
          wrapHeaderText: true,
          autoHeaderHeight: true,
          cellRenderer: (params: any) => {
            if (!params.value) return <StatusBadge status={"N/A"} />;
            const truncated =
              params.value.length > 40
                ? params.value.substring(0, 40) + "..."
                : params.value;
            return <span title={params.value}>{truncated}</span>;
          },
        } as ColDef;
      }

      return null;
    })
    .filter(Boolean) as ColDef[];

  // Final order: Base → ReturnIds → Actions → UserActions
  return [...transformedBase, ...transformedActions, ...transformedUserAction];
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

/**
 * FlowReport Component
 *
 * Generic flow report component that supports both single-flow and multi-flow modes.
 *
 * Single Mode:
 * - Uses FlowId parameter
 * - Endpoints: /workflow/flow/report/, /workflow/flow/grid/
 * - Row identifier: returnId
 *
 * Multi Mode:
 * - Uses reportType parameter
 * - Endpoints: /workflow/flow/multi-report/, /workflow/flow/multi-grid/
 * - Row identifier: _joinKey (CygnusExecutionId)
 */
export const FlowReport: React.FC<FlowReportProps> = ({
  reportMode,
  flowId,
  reportType,
  multiFlowConfig,
  flowName,
  reportEndpoint = "/workflow/flow/report/",
  gridEndpoint = "/workflow/flow/grid/",
  onReportLoaded,
  onError,
  loadingComponent,
  errorComponent,
  initialPageSize = 50,
  showHeader = true,
  showActionStats = true,
  xaxistitle,
  columnTransformer,
  customFilterKeyMap,
  beautifiedNames: propBeautifiedNames,
  baseColumnHeaders,
  disclaimerText,
}) => {
  // ==========================================================================
  // STATE
  // ==========================================================================

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reportData, setReportData] = useState<FlowReportData | null>(null);
  const [gridColumns, setGridColumns] = useState<GridColumn[]>([]);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<FlowReportGridRow | undefined>();

  // Beautified names from API
  const [apiBeautifiedNames, setApiBeautifiedNames] = useState<
    Record<string, string>
  >({});
  const [userActionStatuses, setUserActionStatuses] = useState<string[]>([]);
  // Date filters
  const [fromDate, setFromDate] = useState<Date | null>(getStartOfYear);
  const [toDate, setToDate] = useState<Date | null>(getToday);
  const [appliedFromDate, setAppliedFromDate] = useState<Date | null>(
    getStartOfYear,
  );
  const [appliedToDate, setAppliedToDate] = useState<Date | null>(getToday);

  // Grid state
  const [cachedData, setCachedData] = useState<any[]>([]);
  const [gridLoading, setGridLoading] = useState(false);
  const gridRef = useRef<CygnusGridRef>(null);

  // Refresh trigger
  const [refreshKey, setRefreshKey] = useState(0);
  const token = localStorage.getItem("token");

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================

  // Determine mode
  const isMultiMode = reportMode === "multi";

  // Flows configuration
  const flowsConfig = useMemo(
    () => multiFlowConfig?.flows || [],
    [multiFlowConfig],
  );

  // Merged beautified names
  const beautifiedNames = useMemo(
    () => ({ ...propBeautifiedNames, ...apiBeautifiedNames }),
    [propBeautifiedNames, apiBeautifiedNames],
  );

  // ==========================================================================
  // CALLBACKS
  // ==========================================================================

  const handleRefresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    gridRef.current?.refreshGrid();
  }, []);

  /**
   * Handle user action update from detail modal
   * Refreshes grid to show updated status
   */
  const handleUserActionUpdate = useCallback(
    (returnId: number, status: string, comment: string) => {
      console.log("[FlowReport] User action updated:", {
        returnId,
        status,
        comment,
      });
      // Refresh grid to show updated status
      gridRef.current?.refreshGrid();
    },
    [],
  );

  /**
   * Fetch Report Data
   */
  const fetchReportData = useCallback(async () => {
    const fromIso = toDateOnlyIso(appliedFromDate);
    const toIso = toDateOnlyIso(appliedToDate);

    const params = new URLSearchParams();

    if (isMultiMode) {
      params.set("reportType", reportType || "");
    } else {
      params.set("FlowId", String(flowId));
      params.set("reportType", reportType || "");
    }

    if (fromIso) params.set("FromDate", fromIso);
    if (toIso) params.set("ToDate", toIso);

    const response = await fetch(
      `${process.env.REACT_APP_BASE_URI}${reportEndpoint}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
          "Content-Type": "application/json",
        },
      },
    );

    if (response.status === 401) {
      logout();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const json = await response.json();
    if (!json.success) {
      throw new Error(json.message || "Failed to fetch report data");
    }

    return json.data as FlowReportData;
  }, [
    flowId,
    reportType,
    isMultiMode,
    appliedFromDate,
    appliedToDate,
    reportEndpoint,
    token,
  ]);

  // Store date filters in ref for grid callback
  const dateFiltersRef = useRef({ from: appliedFromDate, to: appliedToDate });
  useEffect(() => {
    dateFiltersRef.current = { from: appliedFromDate, to: appliedToDate };
  }, [appliedFromDate, appliedToDate]);

  /**
   * Fetch Grid Data (used by CygnusGrid)
   */
  const fetchGridData = useCallback(
    async (
      gridParams: GridApiProps,
    ): Promise<{ records: any[]; totalCount: number }> => {
      const { offset, limit, sortModel, filter } = gridParams;
      const fromIso = toDateOnlyIso(dateFiltersRef.current.from);
      const toIso = toDateOnlyIso(dateFiltersRef.current.to);

      const params = new URLSearchParams({
        Page: String(Math.floor(offset / limit) + 1),
        PageSize: String(limit),
      });

      if (isMultiMode) {
        params.set("reportType", reportType || "");
      } else {
        params.set("FlowId", String(flowId));
        params.set("reportType", reportType || "");
      }

      if (fromIso) params.set("FromDate", fromIso);
      if (toIso) params.set("ToDate", toIso);

      if (sortModel && sortModel.length > 0) {
        params.set("SortBy", sortModel[0].colId);
        params.set("SortDir", sortModel[0].sort?.toUpperCase() || "DESC");
      }

      if (filter) {
        Object.entries(filter).forEach(([key, value]: [string, any]) => {
          if (value === null || value === undefined || value === "") return;

          const paramKey = customFilterKeyMap?.[key] || key;

          if (typeof value === "object" && value?.filterType === "date") {
            if (value.dateFrom || value.filter) {
              const dateOnly = value.dateFrom.split(" ")[0];
              params.set(paramKey, dateOnly);
            }
          } else {
            append(paramKey, value, params);
          }
        });
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URI}${gridEndpoint}?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json",
            },
          },
        );

        if (response.status === 401) {
          logout();
          throw new Error("Session expired. Please login again.");
        }

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const json = await response.json();
        if (json.success) {
          const gridData = json.data.grid as FlowReportGrid;

          if (json.data.beautifiedNames) {
            setApiBeautifiedNames(json.data.beautifiedNames);
          }

          if (json.data.userActionStatuses) {
            setUserActionStatuses(json.data.userActionStatuses);
          }

          return {
            records: gridData.rows,
            totalCount: gridData.rowCount,
          };
        }
      } catch (err) {
        console.error("[FlowReport] fetchGridData error:", err);
      }

      return { records: [], totalCount: 0 };
    },
    [flowId, reportType, isMultiMode, gridEndpoint, customFilterKeyMap, token],
  );

  /**
   * Fetch Grid Columns
   */
  const fetchGridColumns = useCallback(async () => {
    const fromIso = toDateOnlyIso(appliedFromDate);
    const toIso = toDateOnlyIso(appliedToDate);

    const params = new URLSearchParams({
      Page: "1",
      PageSize: "1",
    });

    if (isMultiMode) {
      params.set("reportType", reportType || "");
    } else {
      params.set("FlowId", String(flowId));
      params.set("reportType", reportType || "");
    }

    if (fromIso) params.set("FromDate", fromIso);
    if (toIso) params.set("ToDate", toIso);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URI}${gridEndpoint}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        },
      );

      if (response.ok) {
        const json = await response.json();
        if (json.success && json.data?.grid?.columns) {
          if (json.data.beautifiedNames) {
            setApiBeautifiedNames(json.data.beautifiedNames);
          }
          if (json.data.userActionStatuses) {
            setUserActionStatuses(json.data.userActionStatuses);
          }
          return json.data.grid.columns as GridColumn[];
        }
      }
    } catch (err) {
      console.error("[FlowReport] Failed to fetch grid columns:", err);
    }
    return [];
  }, [
    flowId,
    reportType,
    isMultiMode,
    appliedFromDate,
    appliedToDate,
    gridEndpoint,
    token,
  ]);

  // ==========================================================================
  // EFFECTS
  // ==========================================================================

  /**
   * Initial data load
   */
  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        const [fetchedReportData, columns] = await Promise.all([
          fetchReportData(),
          fetchGridColumns(),
        ]);

        if (flowName && fetchedReportData.report?.meta) {
          fetchedReportData.report.meta.title = flowName;
        }

        if (fetchedReportData.beautifiedNames) {
          setApiBeautifiedNames(fetchedReportData.beautifiedNames);
        }

        if (!cancelled) {
          setReportData(fetchedReportData);
          setGridColumns(columns);

          if (onReportLoaded && fetchedReportData.report) {
            onReportLoaded(fetchedReportData.report, null);
          }
        }
      } catch (err) {
        console.error("[FlowReport] Initial load error:", err);
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error("Unknown error");
          setError(error);
          if (onError) onError(error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadData();
    return () => {
      cancelled = true;
    };
  }, [
    flowId,
    reportType,
    flowName,
    appliedFromDate,
    appliedToDate,
    refreshKey,
    fetchReportData,
    fetchGridColumns,
    onReportLoaded,
    onError,
  ]);

  /**
   * Refresh grid when date filters change
   */
  const prevDatesRef = useRef({ from: appliedFromDate, to: appliedToDate });
  useEffect(() => {
    if (
      prevDatesRef.current.from === appliedFromDate &&
      prevDatesRef.current.to === appliedToDate
    ) {
      return;
    }
    prevDatesRef.current = { from: appliedFromDate, to: appliedToDate };

    if (gridRef.current) {
      gridRef.current.refreshGrid();
    }
  }, [appliedFromDate, appliedToDate]);

  // ==========================================================================
  // MEMOIZED VALUES
  // ==========================================================================

  /**
   * Filter key map
   */
  const filterKeyMap = useMemo(() => {
    if (customFilterKeyMap) return customFilterKeyMap;

    const map: Record<string, string> = {};
    gridColumns.forEach((col) => {
      map[col.field] = col.field;
    });
    return map;
  }, [gridColumns, customFilterKeyMap]);

  /**
   * Handle opening detail modal from grid cell click
   */
  const handleOpenDetail = useCallback((row: FlowReportGridRow) => {
    setDetailRow(row);
    setDetailOpen(true);
  }, []);

  /**
   * Column definitions for AG-Grid
   */
  const columnDefs: ColDef[] = useMemo(() => {
    if (gridColumns.length === 0) return [];

    const transformer = columnTransformer || defaultColumnTransformer;
    return transformer(gridColumns, {
      formatDateLocalFull,
      formatDateLocal,
      StatusBadge,
      StatusDateCell,
      beautifiedNames,
      baseColumnHeaders: baseColumnHeaders || {},
      flowsConfig,
      userActionStatuses,
      onRowClick: handleOpenDetail,
    });
  }, [
    gridColumns,
    columnTransformer,
    beautifiedNames,
    baseColumnHeaders,
    flowsConfig,
    userActionStatuses,
    handleOpenDetail,
  ]);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // Loading state
  if (loading) {
    return (
      <>
        {showHeader && (
          <div className={styles.header}>
            <div className={styles.title}>Loading...</div>
          </div>
        )}
        <div className={styles.body}>
          {loadingComponent || (
            <div className={styles.loading}>Loading report…</div>
          )}
        </div>
      </>
    );
  }

  // Toast or notification for export feedback
  const handleExportStart = (format: ExportFormat) => {
    toast.info(`Starting ${format.toUpperCase()} export...`);
  };

  const handleExportComplete = (format: ExportFormat, success: boolean) => {
    if (success) {
      toast.success(`${format.toUpperCase()} export completed!`);
    } else {
      toast.error(`${format.toUpperCase()} export failed. Please try again.`);
    }
  };

  // Error state
  if (error) {
    return (
      <>
        {showHeader && (
          <div className={styles.header}>
            <div className={styles.title}>Error</div>
          </div>
        )}
        <div className={styles.body}>
          {errorComponent ? (
            errorComponent(error)
          ) : (
            <div className={styles.empty}>
              Error loading report: {error.message}
            </div>
          )}
        </div>
      </>
    );
  }

  const report = reportData?.report;
  const actionStats = reportData?.actionStats;
  const title = report?.meta?.title ?? flowName ?? `Flow Report`;
  const subtitle = report?.meta?.subtitle ?? "Execution Report";

  return (
    <>
      {/* Header */}
      {showHeader && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>
          <div className={styles.headerRight}>
            <div className={styles.dateFilter}>
              <div className={styles.dateField}>
                <label>From</label>
                {/* @ts-ignore */}
                <DateTime
                  id="fromDate"
                  label=""
                  placeholder="Select date"
                  example={null}
                  value={fromDate}
                  onChange={setFromDate}
                  disabled={false}
                  hidden={false}
                  mandatory={false}
                  md={12}
                />
              </div>
              <div className={styles.dateField}>
                <label>To</label>
                {/* @ts-ignore */}
                <DateTime
                  id="toDate"
                  label=""
                  placeholder="Select date"
                  example={null}
                  value={toDate}
                  onChange={setToDate}
                  disabled={false}
                  hidden={false}
                  mandatory={false}
                  md={12}
                />
              </div>
              <button
                className={styles.applyBtn}
                onClick={() => {
                  setAppliedFromDate(fromDate);
                  setAppliedToDate(toDate);
                }}
                disabled={!fromDate && !toDate}
                title="Apply date filters"
              >
                Apply
              </button>
              <button
                className={styles.clearBtn}
                onClick={() => {
                  setFromDate(null);
                  setToDate(null);
                  setAppliedFromDate(null);
                  setAppliedToDate(null);
                }}
                disabled={
                  !fromDate && !toDate && !appliedFromDate && !appliedToDate
                }
                title="Clear filters"
              >
                Clear
              </button>
              <button
                className={styles.refreshBtn}
                onClick={handleRefresh}
                title="Refresh report data"
              >
                <Icons.ArrowClockwise size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className={styles.body}>
        {/* Disclaimer */}

          <div className={styles.disclaimer}>
            <Icons.InfoCircle size={16} />
            <span>NOTE: All times are displayed in <b>{getTimezoneDisplay()}</b>. {disclaimerText && disclaimerText}</span>
          </div>

        {/* Summary + Action Stats Chart */}
        {showActionStats && actionStats && actionStats.length > 0 ? (
          <div className={styles.summaryChartLayout}>
            <div className={styles.summaryColumn}>
              {!report || !report.sections?.length ? (
                <div className={styles.empty}>No report data available.</div>
              ) : (
                report.sections.map((section: ReportSection) => {
                  if (section.visible === false) return null;
                  return (
                    <div key={section.id} className={styles.section}>
                      <div className={styles.sectionTitle}>{section.title}</div>
                      {section.subtitle && (
                        <div className={styles.sectionSubtitle}>
                          {section.subtitle}
                        </div>
                      )}
                      <div className={styles.stackedItems}>
                        {section.items.map((item: ReportItem) => {
                          if (item.visible === false) return null;
                          if (item.type === "metric")
                            return <MetricCard key={item.id} {...item} />;
                          if (item.type === "chip")
                            return <Chip key={item.id} {...item} />;
                          return null;
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className={styles.chartColumn}>
              <div className={styles.section}>
                <div className={styles.sectionTitle}>
                  ACTION-LEVEL STATISTICS
                </div>
                <div className={styles.sectionSubtitle}>
                  Success and failure breakdown by workflow action
                </div>
                <ActionStatsChart
                  stats={actionStats}
                  beautifiedNames={beautifiedNames}
                  xaxistitle={xaxistitle}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {!report || !report.sections?.length ? (
              <div className={styles.empty}>No report data available.</div>
            ) : (
              report.sections.map((section: ReportSection) => {
                if (section.visible === false) return null;
                return (
                  <div key={section.id} className={styles.section}>
                    <div className={styles.sectionTitle}>{section.title}</div>
                    {section.subtitle && (
                      <div className={styles.sectionSubtitle}>
                        {section.subtitle}
                      </div>
                    )}
                    <div className={styles.stackedItems}>
                      {section.items.map((item: ReportItem) => {
                        if (item.visible === false) return null;
                        if (item.type === "metric")
                          return <MetricCard key={item.id} {...item} />;
                        if (item.type === "chip")
                          return <Chip key={item.id} {...item} />;
                        return null;
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* Grid Section */}
        {columnDefs.length > 0 && (
          <div className={styles.section}>
            <div className={styles.gridContainer}>
              <CygnusGrid
                key={`flow-grid-${isMultiMode ? reportType : flowId}-${gridColumns.length}`}
                ref={gridRef}
                columnDefs={columnDefs}
                fetchData={fetchGridData}
                filterKeyMap={filterKeyMap}
                rowSelection={undefined}
                dataIdentifier={isMultiMode ? "_joinKey" : "returnid"}
                pageSize={initialPageSize}
                floatingFilters={true}
                cachedData={cachedData}
                setCachedData={setCachedData}
                cachedIsLoading={gridLoading}
                onLoadingChange={setGridLoading}
                rowModelType="serverSide"
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 100,
                  suppressFloatingFilterButton: true,
                  wrapHeaderText: true,
                  autoHeaderHeight: true,
                }}
                // Export configuration - dynamic based on mode
                gridKey={isMultiMode ? "multi_flow_report" : "flow_report"}
                gridParams={{
                  // Common params
                  reportType: reportType,
                  fromDate: appliedFromDate
                    ? toDateOnlyIso(appliedFromDate)
                    : null,
                  toDate: appliedToDate ? toDateOnlyIso(appliedToDate) : null,
                  // Single mode only
                  ...(isMultiMode ? {} : { flowId: flowId }),
                }}
                onExportStart={handleExportStart}
                onExportComplete={handleExportComplete}
                enableFullExport={true}
              />
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {detailOpen && detailRow && (
        <Modal
          show={detailOpen}
          onHide={() => setDetailOpen(false)}
          centered
          size="xl"
        >
          <Modal.Header closeButton>
            {/* <Modal.Title>Record Details</Modal.Title> */}
          </Modal.Header>
          <Modal.Body>
            <DetailModalBody
              row={detailRow}
              flowsConfig={flowsConfig}
              reportMode={reportMode}
              beautifiedNames={beautifiedNames}
              onUserActionUpdate={handleUserActionUpdate}
              onRefresh={handleRefresh}
            />
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default FlowReport;
